import { DateTimeInput, Field, NumericInput, RadioGroup, TextInput, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import { DateTimeStyle } from 'Components/Layout/Grid/types';
import {
  IWorksheetComponent,
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { getDryMassGridFieldSet } from 'Components/Worksheets/MoistureContentWorksheet/As/1289_2_1_1/gridFieldSets';
import { DryingEquipment } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import React from 'react';
import { useSelector } from 'react-redux';
import { pipe } from 'rxjs';

let SievePortionPreparationSectionDryingBeforeSeparationWithOvenDry: React.FC<IWorksheetComponent> = ({
  createWorksheetSelector,
}) => {
  const dryingEquipment = useSelector(
    createWorksheetSelector!('MassMeasurement.MeasuredConstantDryMass.Equipment.DryingEquipment')
  ) as DryingEquipment;

  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));

  const showTimeInOven = useSelector(
    pipe(createDryMassEntryOptionSelector(createWorksheetSelector!), (v) => v !== 'Never')
  );

  const showSingleReadingSection = useSelector(
    pipe(
      createWorksheetSelector!('MassMeasurement.MeasuredConstantDryMass.DryingTray.DryMassReadings'),
      (r) => (r?.length ?? 0) <= 1 && dryMassEntryOption === 'Never'
    )
  );

  return (
    <>
      <NamePrefixModifier appendNamePrefix="MassMeasurement">
        <ListFieldSection>
          <WorksheetBalanceFieldSet name="Equipment.Balance" />
          <Field input={<TextInput />} name="TareID" />
          <Field input={<NumericInput />} name="TareMass" />
          <Field input={<NumericInput />} name="TareAndWetMass" />
          <Field input={<TextOutput />} name="WetMass" />
        </ListFieldSection>
      </NamePrefixModifier>
      <NamePrefixModifier appendNamePrefix="MassMeasurement.MeasuredConstantDryMass">
        <ListFieldSection heading="Sieve Portion Dry Mass">
          <Field
            input={<RadioGroup />}
            name="Equipment.DryingEquipment"
            options={[
              { label: 'Oven', value: DryingEquipment.Oven },
              { label: 'Microwave Oven', value: DryingEquipment.MicrowaveOven },
              { label: 'Infrared Light', value: DryingEquipment.InfraredLight },
              { label: 'Hotplate', value: DryingEquipment.Hotplate },
            ]}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="Equipment.Oven"
            equipmentType="oven"
            visible={dryingEquipment === DryingEquipment.Oven}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="Equipment.MicrowaveOven"
            equipmentType="microwave-oven"
            visible={dryingEquipment === DryingEquipment.MicrowaveOven}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="Equipment.InfraredLight"
            equipmentType="infrared-light"
            visible={dryingEquipment === DryingEquipment.InfraredLight}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="Equipment.Hotplate"
            equipmentType="hotplate"
            visible={dryingEquipment === DryingEquipment.Hotplate}
          />
        </ListFieldSection>
        <ListFieldSection>
          <Field
            input={<NumericInput />}
            name="Equipment.DryingTemperature"
            visible={dryingEquipment === DryingEquipment.Oven}
          />
          {showTimeInOven && <Field input={<DateTimeInput />} name="DryingTray.DateTimePlacedInOvenLocal" />}
          <WorksheetBalanceFieldSet name="Equipment.DryMassBalance" />
        </ListFieldSection>
        {showSingleReadingSection ? (
          <ListFieldSection>
            <Field
              input={<NumericInput />}
              name="DryingTray.DryMassReadings[0].DrySoilAndContainerMass"
              label="Tare and Material Dry Mass"
            />
          </ListFieldSection>
        ) : (
          <WorksheetGridFieldSection name="DryingTray.DryMassReadings">
            <WorksheetRecordGrid
              name="DryingTray.DryMassReadings"
              orientation="vertical"
              recordProps={{
                enforceAtLeastOneRecord: true,
                preventRemove: 'all-but-last',
                removeConfirm: {
                  confirm: true,
                  confirmTitle: 'Delete Dry Mass Reading',
                  confirmText: 'Are you sure you want to delete the dry mass reading? Data cannot be recovered.',
                  confirmButtonText: 'Delete',
                },
              }}
              fieldSet={getDryMassGridFieldSet(dryMassEntryOption, DateTimeStyle.Combined)}
            />
          </WorksheetGridFieldSection>
        )}
        <ListFieldSection>
          <Field input={<TextOutput />} name="Measurements.TotalDryMass" />
        </ListFieldSection>
      </NamePrefixModifier>
    </>
  );
};

SievePortionPreparationSectionDryingBeforeSeparationWithOvenDry = withInjectedProps(
  SievePortionPreparationSectionDryingBeforeSeparationWithOvenDry
);

export { SievePortionPreparationSectionDryingBeforeSeparationWithOvenDry };
export default SievePortionPreparationSectionDryingBeforeSeparationWithOvenDry;
